import React, { useState, useRef, useEffect } from 'react';
import { ClockIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { createPortal } from 'react-dom';
import { useClickOutside } from 'services/useClickOutside';
import { PLANNER_TIME_FORMAT_24, plannerTimeUses24Hour } from '../moduleServices/planner_time_format';

// ##### TimePicker Component - Reusable time picker with clock dropdown
// Supports both single time and time range (start/end)
const TimePicker = ({ 
  value = '', // Can be string 'HH:MM' or object { start: 'HH:MM', end: 'HH:MM' }
  onChange, 
  name = 'time',
  disabled = false,
  required = false,
  className = '',
  placeholder = 'Select time',
  timeFormatPreference = PLANNER_TIME_FORMAT_24,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const is24HourMode = plannerTimeUses24Hour(timeFormatPreference);
  // Start time states
  const [selectedHourStart, setSelectedHourStart] = useState(12);
  const [selectedMinuteStart, setSelectedMinuteStart] = useState(0);
  const [isAMStart, setIsAMStart] = useState(true);
  
  // End time states
  const [selectedHourEnd, setSelectedHourEnd] = useState(12);
  const [selectedMinuteEnd, setSelectedMinuteEnd] = useState(0);
  const [isAMEnd, setIsAMEnd] = useState(true);
  
  const [activeSection, setActiveSection] = useState('hourStart'); // 'hourStart', 'minuteStart', 'hourEnd', 'minuteEnd'
  const [isAMPMOpenStart, setIsAMPMOpenStart] = useState(false);
  const [isAMPMOpenEnd, setIsAMPMOpenEnd] = useState(false);
  const [hasEndTime, setHasEndTime] = useState(false); // Whether end time is enabled (default: false/unchecked)
  const [tempTimeValue, setTempTimeValue] = useState(null); // Store temporary time before applying { start, end }
  const [errorMessage, setErrorMessage] = useState(null); // Store validation error message
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);
  const ampmDropdownRefStart = useRef(null);
  const ampmDropdownRefEnd = useRef(null);

  // ##### Handle click outside to close dropdowns
  useClickOutside([buttonRef, dropdownRef], () => setIsOpen(false), isOpen);
  useClickOutside([ampmDropdownRefStart], () => setIsAMPMOpenStart(false), isAMPMOpenStart);
  useClickOutside([ampmDropdownRefEnd], () => setIsAMPMOpenEnd(false), isAMPMOpenEnd);

  // ##### Check if value is range mode (object with start/end)
  const isRangeMode = () => {
    return value && typeof value === 'object' && (value.start || value.end);
  };

  // ##### Initialize from value if provided, or when opening dropdown
  useEffect(() => {
    if (isOpen) {
      setErrorMessage(null); // Clear any error when opening dropdown
      const rangeMode = isRangeMode();
      
      if (rangeMode && (value.start || value.end)) {
        // Range mode: initialize both start and end
        const startTime = value.start || '12:00';
        const endTime = value.end || '12:00';
        
        // Check if end time is different from start (hasEndTime = true) or same (hasEndTime = false)
        const endTimeEnabled = startTime !== endTime;
        setHasEndTime(endTimeEnabled);
        
        // Initialize start time
        const [startHours, startMinutes] = startTime.split(':').map(Number);
        if (!isNaN(startHours) && !isNaN(startMinutes)) {
          if (is24HourMode) {
            setSelectedHourStart(startHours);
          } else {
            const hour12 = startHours === 0 ? 12 : startHours > 12 ? startHours - 12 : startHours;
            setSelectedHourStart(hour12);
            setIsAMStart(startHours < 12);
          }
          setSelectedMinuteStart(startMinutes);
        }
        
        // Initialize end time (use start if end is disabled)
        const timeToUse = endTimeEnabled ? endTime : startTime;
        const [endHours, endMinutes] = timeToUse.split(':').map(Number);
        if (!isNaN(endHours) && !isNaN(endMinutes)) {
          if (is24HourMode) {
            setSelectedHourEnd(endHours);
          } else {
            const hour12 = endHours === 0 ? 12 : endHours > 12 ? endHours - 12 : endHours;
            setSelectedHourEnd(hour12);
            setIsAMEnd(endHours < 12);
          }
          setSelectedMinuteEnd(endMinutes);
        }
        
        setTempTimeValue({ start: startTime, end: endTimeEnabled ? endTime : startTime });
      } else if (value && typeof value === 'string') {
        // Single time mode (backward compatibility)
        setHasEndTime(false); // Single time = no end time
        const [hours, minutes] = value.split(':').map(Number);
        if (!isNaN(hours) && !isNaN(minutes)) {
          if (is24HourMode) {
            setSelectedHourStart(hours);
            setSelectedHourEnd(hours);
          } else {
            const hour12 = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
            setSelectedHourStart(hour12);
            setSelectedHourEnd(hour12);
            setIsAMStart(hours < 12);
            setIsAMEnd(hours < 12);
          }
          setSelectedMinuteStart(minutes);
          setSelectedMinuteEnd(minutes);
          setTempTimeValue({ start: value, end: value });
        }
      } else {
        // Reset to default when opening without value
        setHasEndTime(false); // Default: unchecked
        if (is24HourMode) {
          setSelectedHourStart(0);
          setSelectedHourEnd(0);
        } else {
          setSelectedHourStart(12);
          setSelectedHourEnd(12);
          setIsAMStart(true);
          setIsAMEnd(true);
        }
        setSelectedMinuteStart(0);
        setSelectedMinuteEnd(0);
        setActiveSection('hourStart');
        setTempTimeValue(null);
      }
    }
  }, [isOpen, value, is24HourMode]);

  // ##### Format single time string for display
  const formatSingleTime = (timeString) => {
    if (!timeString) return '';
    const [hours, minutes] = timeString.split(':').map(Number);
    if (isNaN(hours) || isNaN(minutes)) return '';
    
    const minStr = String(minutes).padStart(2, '0');
    
    if (is24HourMode) {
      // 24-hour format: HH:MM
      const hourStr = String(hours).padStart(2, '0');
      return `${hourStr}:${minStr}`;
    } else {
      // 12-hour format: HH:MM AM/PM
      const hour12 = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
      const ampm = hours < 12 ? 'AM' : 'PM';
      return `${hour12}:${minStr} ${ampm}`;
    }
  };

  // ##### Format time for display (supports range mode)
  const formatDisplayTime = (value) => {
    if (!value) return placeholder;
    
    const rangeMode = isRangeMode();
    
    if (rangeMode) {
      const start = value.start || '';
      const end = value.end || '';
      if (!start && !end) return placeholder;
      
      const startFormatted = formatSingleTime(start);
      const endFormatted = formatSingleTime(end);
      
      // If start and end are equal, show only start
      if (start === end) {
        return startFormatted || placeholder;
      }
      
      // Show both: "12:00 PM - 13:00 PM"
      if (startFormatted && endFormatted) {
        return (
          <span>
            <strong>{startFormatted}</strong> - <strong>{endFormatted}</strong>
          </span>
        );
      }
      return startFormatted || endFormatted || placeholder;
    } else {
      // Single time mode (backward compatibility)
      if (typeof value === 'string') {
        const formatted = formatSingleTime(value);
        return formatted || placeholder;
      }
      return placeholder;
    }
  };


  // ##### Convert hour/minute/am to 24-hour time string
  // Assumes values are already validated
  const toTimeString = (hour, minute, am) => {
    // Handle empty or invalid values - use defaults
    const h = (hour === '' || isNaN(hour)) ? (is24HourMode ? 0 : 12) : hour;
    const m = (minute === '' || isNaN(minute)) ? 0 : minute;
    
    let hour24;
    if (is24HourMode) {
      hour24 = h;
    } else {
      hour24 = h;
      if (am && h === 12) hour24 = 0;
      else if (!am && h !== 12) hour24 = h + 12;
    }
    return `${String(hour24).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  };

  // ##### Update temp time value from current states (only if values are valid)
  const updateTempTimeValue = () => {
    // Only update if all values are valid
    if (isValidHour(selectedHourStart) && isValidMinute(selectedMinuteStart) &&
        isValidHour(selectedHourEnd) && isValidMinute(selectedMinuteEnd)) {
      const startTime = toTimeString(selectedHourStart, selectedMinuteStart, isAMStart);
      const endTime = toTimeString(selectedHourEnd, selectedMinuteEnd, isAMEnd);
      setTempTimeValue({ start: startTime, end: endTime });
    }
  };

  // ##### Validate hour value
  const isValidHour = (hour) => {
    if (isNaN(hour)) return false;
    if (is24HourMode) {
      return hour >= 0 && hour <= 23;
    } else {
      return hour >= 1 && hour <= 12;
    }
  };

  // ##### Validate minute value
  const isValidMinute = (minute) => {
    if (isNaN(minute)) return false;
    return minute >= 0 && minute <= 59;
  };

  // ##### Handle hour input change (start) - no validation, just update state
  const handleHourChangeStart = (e) => {
    e.stopPropagation();
    const value = e.target.value === '' ? '' : parseInt(e.target.value);
    setSelectedHourStart(isNaN(value) ? '' : value);
    setErrorMessage(null); // Clear error when user types
  };

  // ##### Handle minute input change (start) - no validation, just update state
  const handleMinuteChangeStart = (e) => {
    e.stopPropagation();
    const value = e.target.value === '' ? '' : parseInt(e.target.value);
    setSelectedMinuteStart(isNaN(value) ? '' : value);
    setErrorMessage(null); // Clear error when user types
  };

  // ##### Handle hour input change (end) - no validation, just update state
  const handleHourChangeEnd = (e) => {
    e.stopPropagation();
    const value = e.target.value === '' ? '' : parseInt(e.target.value);
    setSelectedHourEnd(isNaN(value) ? '' : value);
    setErrorMessage(null); // Clear error when user types
  };

  // ##### Handle minute input change (end) - no validation, just update state
  const handleMinuteChangeEnd = (e) => {
    e.stopPropagation();
    const value = e.target.value === '' ? '' : parseInt(e.target.value);
    setSelectedMinuteEnd(isNaN(value) ? '' : value);
    setErrorMessage(null); // Clear error when user types
  };

  // ##### Handle AM/PM selection (start)
  const handleAMPMSelectStart = (am) => {
    setIsAMStart(am);
    setIsAMPMOpenStart(false);
    setErrorMessage(null); // Clear error when changing AM/PM
    updateTempTimeValue();
  };

  // ##### Handle AM/PM selection (end)
  const handleAMPMSelectEnd = (am) => {
    setIsAMEnd(am);
    setIsAMPMOpenEnd(false);
    setErrorMessage(null); // Clear error when changing AM/PM
    updateTempTimeValue();
  };

  // ##### Handle apply - validate and save
  const handleApply = (e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }

    // Validate all inputs
    let error = null;

    // Validate start hour
    if (selectedHourStart === '' || !isValidHour(selectedHourStart)) {
      error = 'Invalid hour (start)';
    }
    // Validate start minute
    else if (selectedMinuteStart === '' || !isValidMinute(selectedMinuteStart)) {
      error = 'Invalid minute (start)';
    }
    // Validate end hour (only if end time is enabled)
    else if (hasEndTime && (selectedHourEnd === '' || !isValidHour(selectedHourEnd))) {
      error = 'Invalid hour (end)';
    }
    // Validate end minute (only if end time is enabled)
    else if (hasEndTime && (selectedMinuteEnd === '' || !isValidMinute(selectedMinuteEnd))) {
      error = 'Invalid minute (end)';
    }

    if (error) {
      setErrorMessage(error);
      return; // Don't close or apply if validation fails
    }

    // All valid, update temp value and apply
    const startTime = toTimeString(selectedHourStart, selectedMinuteStart, isAMStart);
    // If end time is disabled, use start time as end
    const endTime = hasEndTime ? toTimeString(selectedHourEnd, selectedMinuteEnd, isAMEnd) : startTime;
    const timeValue = { start: startTime, end: endTime };

    if (onChange) {
      onChange({ target: { name: name, value: timeValue } });
    }
    setErrorMessage(null);
    setIsOpen(false);
  };

  // ##### Handle end time checkbox toggle
  const handleEndTimeToggle = (e) => {
    e.stopPropagation();
    // Don't preventDefault - let the checkbox handle its own state change
    const newHasEndTime = !hasEndTime;
    setHasEndTime(newHasEndTime);
    setErrorMessage(null); // Clear error when toggling
    
    // If disabling end time, sync end values with start
    if (!newHasEndTime) {
      setSelectedHourEnd(selectedHourStart);
      setSelectedMinuteEnd(selectedMinuteStart);
      setIsAMEnd(isAMStart);
    }
  };


  // ##### Get portal position
  // Using position: 'fixed' means coordinates are relative to viewport
  // getBoundingClientRect() already returns viewport coordinates, so no need to add scroll
  const getPortalStyle = () => {
    if (!buttonRef.current) return {};
    const rect = buttonRef.current.getBoundingClientRect();
    return {
      position: 'fixed',
      top: `${rect.bottom + 8}px`,
      left: `${rect.left}px`,
      zIndex: 50,
    };
  };



  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          if (!disabled) setIsOpen(!isOpen);
        }}
        disabled={disabled}
        className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-700 disabled:bg-gray-100 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors ${className}`}
      >
        <ClockIcon className="w-4 h-4 text-gray-500 flex-shrink-0" />
        <span className={`text-xs ${value ? 'text-gray-800' : 'text-gray-400'}`}>
          {formatDisplayTime(value)}
        </span>
      </button>

      {/* Time Dropdown */}
      {isOpen && createPortal(
        <div 
          style={getPortalStyle()}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div
            ref={dropdownRef}
            className="bg-white rounded-lg shadow-xl border border-gray-200 p-2.5 w-72"
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            {/* Time Display - Start and End with 4 inputs */}
            <div className="mb-2 pb-2 border-b border-gray-200 space-y-3">
              {/* Start Time */}
              <div>
                <div className="text-xs font-medium text-gray-500 mb-1.5">Start</div>
                <div className="flex items-center justify-center gap-1 text-xl font-semibold text-gray-800">
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      setActiveSection('hourStart');
                    }}
                    className={`px-2 py-0.5 rounded transition-colors cursor-pointer ${
                      activeSection === 'hourStart'
                        ? 'bg-blue-100 text-blue-700'
                        : 'hover:bg-gray-100 text-gray-800'
                    }`}
                  >
                    <input
                      type="number"
                      value={selectedHourStart}
                      onChange={handleHourChangeStart}
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveSection('hourStart');
                      }}
                      onFocus={(e) => {
                        e.stopPropagation();
                        setActiveSection('hourStart');
                      }}
                      className="w-10 text-center bg-transparent border-none outline-none text-xl font-semibold [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    />
                  </div>
                  <span className="text-gray-400">:</span>
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      setActiveSection('minuteStart');
                    }}
                    className={`px-2 py-0.5 rounded transition-colors cursor-pointer ${
                      activeSection === 'minuteStart'
                        ? 'bg-blue-100 text-blue-700'
                        : 'hover:bg-gray-100 text-gray-800'
                    }`}
                  >
                    <input
                      type="number"
                      value={selectedMinuteStart}
                      onChange={handleMinuteChangeStart}
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveSection('minuteStart');
                      }}
                      onFocus={(e) => {
                        e.stopPropagation();
                        setActiveSection('minuteStart');
                      }}
                      className="w-10 text-center bg-transparent border-none outline-none text-xl font-semibold [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    />
                  </div>
                  {/* AM/PM Dropdown Start - Only show in 12-hour mode */}
                  {!is24HourMode && (
                    <div className="ml-1.5 relative" ref={ampmDropdownRefStart}>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          setIsAMPMOpenStart(!isAMPMOpenStart);
                        }}
                        className="px-1.5 py-0.5 text-xs font-semibold text-gray-800 hover:text-gray-900 transition-colors flex items-center gap-1"
                      >
                        {isAMStart ? 'AM' : 'PM'}
                        <ChevronDownIcon className={`w-3 h-3 text-gray-600 transition-transform ${isAMPMOpenStart ? 'rotate-180' : ''}`} />
                      </button>
                      {isAMPMOpenStart && (
                        <div className="absolute top-full mt-1 left-0 bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden z-10 min-w-[60px]">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              handleAMPMSelectStart(true);
                            }}
                            className={`w-full px-2 py-1.5 text-xs font-medium text-left transition-colors ${
                              isAMStart
                                ? 'bg-blue-100 text-blue-700'
                                : 'text-gray-700 hover:bg-gray-100'
                            }`}
                          >
                            AM
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              handleAMPMSelectStart(false);
                            }}
                            className={`w-full px-2 py-1.5 text-xs font-medium text-left transition-colors ${
                              !isAMStart
                                ? 'bg-blue-100 text-blue-700'
                                : 'text-gray-700 hover:bg-gray-100'
                            }`}
                          >
                            PM
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* End Time Checkbox and Inputs */}
              <div>
                <div className="flex items-center gap-2 mb-1.5">
                  <input
                    id="end-time-checkbox"
                    type="checkbox"
                    checked={hasEndTime}
                    onChange={handleEndTimeToggle}
                    onClick={(e) => e.stopPropagation()}
                    className="w-3.5 h-3.5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                  />
                  <label 
                    htmlFor="end-time-checkbox"
                    className="text-xs font-medium text-gray-500 cursor-pointer"
                    onClick={(e) => e.stopPropagation()}
                  >
                    End
                  </label>
                </div>
                {hasEndTime && (
                  <div className="flex items-center justify-center gap-1 text-xl font-semibold text-gray-800">
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      setActiveSection('hourEnd');
                    }}
                    className={`px-2 py-0.5 rounded transition-colors cursor-pointer ${
                      activeSection === 'hourEnd'
                        ? 'bg-blue-100 text-blue-700'
                        : 'hover:bg-gray-100 text-gray-800'
                    }`}
                  >
                    <input
                      type="number"
                      value={selectedHourEnd}
                      onChange={handleHourChangeEnd}
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveSection('hourEnd');
                      }}
                      onFocus={(e) => {
                        e.stopPropagation();
                        setActiveSection('hourEnd');
                      }}
                      className="w-10 text-center bg-transparent border-none outline-none text-xl font-semibold [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    />
                  </div>
                  <span className="text-gray-400">:</span>
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      setActiveSection('minuteEnd');
                    }}
                    className={`px-2 py-0.5 rounded transition-colors cursor-pointer ${
                      activeSection === 'minuteEnd'
                        ? 'bg-blue-100 text-blue-700'
                        : 'hover:bg-gray-100 text-gray-800'
                    }`}
                  >
                    <input
                      type="number"
                      value={selectedMinuteEnd}
                      onChange={handleMinuteChangeEnd}
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveSection('minuteEnd');
                      }}
                      onFocus={(e) => {
                        e.stopPropagation();
                        setActiveSection('minuteEnd');
                      }}
                      className="w-10 text-center bg-transparent border-none outline-none text-xl font-semibold [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    />
                  </div>
                  {/* AM/PM Dropdown End - Only show in 12-hour mode */}
                  {!is24HourMode && (
                    <div className="ml-1.5 relative" ref={ampmDropdownRefEnd}>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          setIsAMPMOpenEnd(!isAMPMOpenEnd);
                        }}
                        className="px-1.5 py-0.5 text-xs font-semibold text-gray-800 hover:text-gray-900 transition-colors flex items-center gap-1"
                      >
                        {isAMEnd ? 'AM' : 'PM'}
                        <ChevronDownIcon className={`w-3 h-3 text-gray-600 transition-transform ${isAMPMOpenEnd ? 'rotate-180' : ''}`} />
                      </button>
                      {isAMPMOpenEnd && (
                        <div className="absolute top-full mt-1 left-0 bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden z-10 min-w-[60px]">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              handleAMPMSelectEnd(true);
                            }}
                            className={`w-full px-2 py-1.5 text-xs font-medium text-left transition-colors ${
                              isAMEnd
                                ? 'bg-blue-100 text-blue-700'
                                : 'text-gray-700 hover:bg-gray-100'
                            }`}
                          >
                            AM
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              handleAMPMSelectEnd(false);
                            }}
                            className={`w-full px-2 py-1.5 text-xs font-medium text-left transition-colors ${
                              !isAMEnd
                                ? 'bg-blue-100 text-blue-700'
                                : 'text-gray-700 hover:bg-gray-100'
                            }`}
                          >
                            PM
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                  </div>
                )}
              </div>
            </div>

            {/* Error Message */}
            {errorMessage && (
              <div className="mt-2 mb-2 text-xs text-red-600 font-medium text-center">
                {errorMessage}
              </div>
            )}

            {/* Apply Button */}
            <div className="mt-2">
              <button
                type="button"
                onClick={handleApply}
                className="w-full px-2 py-1 text-xs bg-blue-700 text-white rounded hover:bg-blue-800 transition-colors font-medium"
              >
                Apply
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default TimePicker;
